import L from 'leaflet';

/*
 * Линейка: измерение расстояний на карте с привязкой к объектам.
 *
 * - Кнопка-линейка включает режим измерения.
 * - Клик/тап ставит точку. Если рядом есть объект (опора, ТП, ПС...),
 *   точка встаёт ровно в его координаты, объект подсвечивается.
 * - У каждого отрезка подпись с длиной (и именами объектов, если привязаны),
 *   в панели — общая длина и кнопки "Назад", "Сбросить", "Закрыть".
 * - Пока режим включён, маркеры и линии не реагируют на нажатия
 *   (не открываются попапы, не срабатывает добавление объекта).
 * - На десктопе от последней точки к курсору тянется линия-превью, Esc — выход.
 */

const SNAP_RADIUS_PX = { mobile: 25, desktop: 15 };
const HIGHLIGHT_CLASS = 'marker-highlight-static'; // тот же класс, что и в HighlightMarker (Main.js)
const LINE_STYLE = { color: '#e11d48', weight: 3, dashArray: '8 6', interactive: false };
const PREVIEW_STYLE = { color: '#e11d48', weight: 2, dashArray: '4 6', opacity: 0.7, interactive: false };

let activeTool = null;

/** Включён ли сейчас режим линейки (для блокировки других обработчиков кликов). */
export function isMeasureModeActive() {
  return !!(activeTool && activeTool.active);
}

/* ------------------------------------------------------------------ */

function injectStyles() {
  if (document.getElementById('measure-tool-styles')) { return; }
  const style = document.createElement('style');
  style.id = 'measure-tool-styles';
  style.textContent = `
    .measure-mode { cursor: crosshair !important; }
    .measure-mode .leaflet-marker-pane *,
    .measure-mode .leaflet-tooltip-pane *,
    .measure-mode .leaflet-popup-pane *,
    .measure-mode .leaflet-overlay-pane * { pointer-events: none !important; }

    .measure-control > .leaflet-bar { display: inline-block; }
    .measure-control a.measure-toggle {
      display: flex; align-items: center; justify-content: center;
      width: 30px; height: 30px; color: #333;
    }
    .measure-control a.measure-toggle.active { background: #e11d48; color: #fff; }

    .measure-panel {
      display: none; margin-top: 6px; padding: 8px 10px; min-width: 170px;
      background: #fff; border-radius: 8px; box-shadow: 0 1px 6px rgba(0,0,0,.3);
      font: 13px/1.35 system-ui, -apple-system, sans-serif; color: #1a1a1a;
      cursor: default;
    }
    .measure-panel.open { display: block; }
    .measure-panel .measure-total { font-size: 16px; font-weight: 600; }
    .measure-panel .measure-hint { color: #666; margin-top: 2px; }
    .measure-panel .measure-buttons { display: flex; gap: 6px; margin-top: 8px; }
    .measure-panel button {
      flex: 1; padding: 6px 4px; border: none; border-radius: 6px;
      background: #f1f1f1; font-size: 12px; cursor: pointer; color: #1a1a1a;
    }
    .measure-panel button:disabled { opacity: .4; cursor: default; }
    .measure-panel.mobile button { padding: 9px 4px; font-size: 13px; }

    .leaflet-tooltip.measure-label {
      background: rgba(255,255,255,.95); border: 1px solid #e11d48; border-radius: 4px;
      padding: 2px 6px; font-size: 12px; font-weight: 600; color: #1a1a1a;
      box-shadow: none; white-space: nowrap; text-align: center;
    }
    .leaflet-tooltip.measure-label::before { display: none; }
    .leaflet-tooltip.measure-label .measure-names { font-weight: 400; color: #555; font-size: 11px; }
  `;
  document.head.appendChild(style);
}

const RULER_SVG = `
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/>
    <path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/>
  </svg>`;

export function formatDistance(meters) {
  if (meters >= 1000) {
    return (meters / 1000).toFixed(2).replace('.', ',') + ' км';
  }
  return meters.toFixed(1).replace('.', ',') + ' м';
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/* ------------------------------------------------------------------ */

const MeasureControl = L.Control.extend({
  options: {
    position: 'topleft',
    isMobile: false,
    onEnable: null,   // вызывается при включении режима (закрыть попапы, отменить рисование и т.п.)
  },

  initialize: function (options) {
    L.setOptions(this, options);
    this.active = false;
    this._points = [];        // { latlng, marker|null }
    this._highlighted = new Set();
    this._previewFrame = null;
  },

  onAdd: function (map) {
    injectStyles();
    this._map = map;

    const container = L.DomUtil.create('div', 'measure-control');
    const bar = L.DomUtil.create('div', 'leaflet-bar', container);
    const toggle = this._toggle = L.DomUtil.create('a', 'measure-toggle', bar);
    toggle.href = '#';
    toggle.title = 'Линейка';
    toggle.innerHTML = RULER_SVG;
    toggle.setAttribute('role', 'button');

    const panel = this._panel = L.DomUtil.create('div', 'measure-panel' + (this.options.isMobile ? ' mobile' : ''), container);
    panel.innerHTML = `
      <div class="measure-total">0 м</div>
      <div class="measure-hint"></div>
      <div class="measure-buttons">
        <button type="button" data-action="undo" title="Убрать последнюю точку">Назад</button>
        <button type="button" data-action="reset">Сбросить</button>
        <button type="button" data-action="close">Закрыть</button>
      </div>`;
    this._totalEl = panel.querySelector('.measure-total');
    this._hintEl = panel.querySelector('.measure-hint');
    this._undoBtn = panel.querySelector('[data-action="undo"]');
    this._resetBtn = panel.querySelector('[data-action="reset"]');

    L.DomEvent.disableClickPropagation(container);
    L.DomEvent.disableScrollPropagation(container);
    L.DomEvent.on(toggle, 'click', (e) => { L.DomEvent.preventDefault(e); this.toggle(); });
    L.DomEvent.on(panel, 'click', (e) => {
      const action = e.target && e.target.getAttribute && e.target.getAttribute('data-action');
      if (action === 'undo') { this.undo(); }
      if (action === 'reset') { this.reset(); }
      if (action === 'close') { this.disable(); }
    });

    this._layers = L.layerGroup();
    activeTool = this;
    return container;
  },

  onRemove: function () {
    this.disable();
    if (activeTool === this) { activeTool = null; }
  },

  toggle: function () {
    if (this.active) { this.disable(); } else { this.enable(); }
  },

  enable: function () {
    if (this.active) { return; }
    const map = this._map;
    if (typeof this.options.onEnable === 'function') { this.options.onEnable(); }
    map.closePopup();

    this.active = true;
    L.DomUtil.addClass(map.getContainer(), 'measure-mode');
    L.DomUtil.addClass(this._toggle, 'active');
    L.DomUtil.addClass(this._panel, 'open');
    this._layers.addTo(map);

    map.on('click', this._onMapClick, this);
    map.on('zoomend', this._reapplyHighlights, this);
    if (!this.options.isMobile) {
      map.on('mousemove', this._onMouseMove, this);
      map.on('mouseout', this._clearPreview, this);
      L.DomEvent.on(document, 'keydown', this._onKeyDown, this);
    }
    this._render();
  },

  disable: function () {
    if (!this.active) { return; }
    const map = this._map;
    this.active = false;

    map.off('click', this._onMapClick, this);
    map.off('zoomend', this._reapplyHighlights, this);
    map.off('mousemove', this._onMouseMove, this);
    map.off('mouseout', this._clearPreview, this);
    L.DomEvent.off(document, 'keydown', this._onKeyDown, this);

    this._points = [];
    this._clearPreview();
    this._clearHighlights();
    this._layers.clearLayers();
    this._layers.remove();

    L.DomUtil.removeClass(map.getContainer(), 'measure-mode');
    L.DomUtil.removeClass(this._toggle, 'active');
    L.DomUtil.removeClass(this._panel, 'open');
  },

  undo: function () {
    this._points.pop();
    this._render();
  },

  reset: function () {
    this._points = [];
    this._render();
  },

  /** Общая длина в метрах (удобно для тестов и, при желании, для внешнего кода). */
  getTotalDistance: function () {
    let total = 0;
    for (let i = 1; i < this._points.length; i++) {
      total += this._map.distance(this._points[i - 1].latlng, this._points[i].latlng);
    }
    return total;
  },

  getPoints: function () {
    return this._points.map(p => ({
      latlng: p.latlng,
      objectId: p.marker ? p.marker.options.id : null,
      objectName: p.marker ? p.marker.options.name : null,
    }));
  },

  /* ---------------- привязка ---------------- */

  _findSnapTarget: function (containerPoint) {
    const map = this._map;
    const radius = this.options.isMobile ? SNAP_RADIUS_PX.mobile : SNAP_RADIUS_PX.desktop;
    let best = null;
    let bestDist = radius;
    map.eachLayer(layer => {
      if (!(layer instanceof L.Marker)) { return; }
      if (!layer.options || layer.options.id === undefined || layer.options.id === null) { return; }
      const d = map.latLngToContainerPoint(layer.getLatLng()).distanceTo(containerPoint);
      if (d <= bestDist) {
        bestDist = d;
        best = layer;
      }
    });
    return best;
  },

  _resolvePoint: function (latlng, containerPoint) {
    const marker = this._findSnapTarget(containerPoint);
    return marker
      ? { latlng: L.latLng(marker.getLatLng()), marker }
      : { latlng: L.latLng(latlng), marker: null };
  },

  _onMapClick: function (e) {
    const point = this._resolvePoint(e.latlng, e.containerPoint);
    const last = this._points[this._points.length - 1];
    // повторный клик в ту же точку (двойной клик, дребезг) — игнорируем
    if (last) {
      const samePlace = this._map.latLngToContainerPoint(last.latlng)
        .distanceTo(this._map.latLngToContainerPoint(point.latlng)) < 3;
      if (samePlace) { return; }
    }
    this._points.push(point);
    this._render();
  },

  /* ---------------- превью на десктопе ---------------- */

  _onMouseMove: function (e) {
    if (!this._points.length) { return; }
    const latlng = e.latlng;
    const containerPoint = e.containerPoint;
    if (this._previewFrame) { return; }
    this._previewFrame = L.Util.requestAnimFrame(() => {
      this._previewFrame = null;
      if (!this.active || !this._points.length) { return; }
      const target = this._resolvePoint(latlng, containerPoint);
      const from = this._points[this._points.length - 1].latlng;
      const dist = this._map.distance(from, target.latlng);

      if (!this._previewLine) {
        this._previewLine = L.polyline([from, target.latlng], PREVIEW_STYLE).addTo(this._map);
        this._previewLabel = L.tooltip({
          permanent: true, direction: 'right', offset: [12, 0],
          className: 'measure-label', interactive: false,
        }).setLatLng(target.latlng).addTo(this._map);
      } else {
        this._previewLine.setLatLngs([from, target.latlng]);
        this._previewLabel.setLatLng(target.latlng);
      }
      const total = this.getTotalDistance() + dist;
      const snapName = target.marker && target.marker.options.name
        ? `<div class="measure-names">${escapeHtml(target.marker.options.name)}</div>` : '';
      this._previewLabel.setContent(
        `${formatDistance(dist)}${this._points.length > 1 ? ` · всего ${formatDistance(total)}` : ''}${snapName}`
      );
    });
  },

  _clearPreview: function () {
    if (this._previewFrame) {
      L.Util.cancelAnimFrame(this._previewFrame);
      this._previewFrame = null;
    }
    if (this._previewLine) { this._previewLine.remove(); this._previewLine = null; }
    if (this._previewLabel) { this._previewLabel.remove(); this._previewLabel = null; }
  },

  _onKeyDown: function (e) {
    if (e.key === 'Escape') { this.disable(); }
    if ((e.key === 'Backspace' || e.key === 'Delete') &&
        !/^(INPUT|TEXTAREA|SELECT)$/.test((e.target && e.target.tagName) || '')) {
      e.preventDefault();
      this.undo();
    }
  },

  /* ---------------- подсветка ---------------- */

  _clearHighlights: function () {
    this._highlighted.forEach(marker => {
      const el = marker.getElement && marker.getElement();
      if (el) { el.classList.remove(HIGHLIGHT_CLASS); }
    });
    this._highlighted.clear();
  },

  _reapplyHighlights: function () {
    // SetupZoomHandler пересоздаёт иконки при зуме — класс подсветки слетает
    this._highlighted.forEach(marker => {
      const el = marker.getElement && marker.getElement();
      if (el) { el.classList.add(HIGHLIGHT_CLASS); }
    });
  },

  /* ---------------- отрисовка ---------------- */

  _render: function () {
    const map = this._map;
    const pts = this._points;
    this._layers.clearLayers();
    this._clearPreview();
    this._clearHighlights();

    pts.forEach(p => {
      if (p.marker) { this._highlighted.add(p.marker); }
      L.circleMarker(p.latlng, {
        radius: p.marker ? 7 : 5,
        color: '#e11d48', weight: 2,
        fillColor: '#fff', fillOpacity: 1,
        interactive: false,
      }).addTo(this._layers);
    });
    this._reapplyHighlights();

    if (pts.length > 1) {
      L.polyline(pts.map(p => p.latlng), LINE_STYLE).addTo(this._layers);
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1], b = pts[i];
        const d = map.distance(a.latlng, b.latlng);
        const mid = L.latLng((a.latlng.lat + b.latlng.lat) / 2, (a.latlng.lng + b.latlng.lng) / 2);
        const aName = a.marker && a.marker.options.name;
        const bName = b.marker && b.marker.options.name;
        const names = aName && bName
          ? `<div class="measure-names">${escapeHtml(aName)} → ${escapeHtml(bName)}</div>` : '';
        L.tooltip({ permanent: true, direction: 'center', className: 'measure-label', interactive: false })
          .setLatLng(mid)
          .setContent(`${formatDistance(d)}${names}`)
          .addTo(this._layers);
      }
    }

    // панель
    this._totalEl.textContent = formatDistance(this.getTotalDistance());
    const tapWord = this.options.isMobile ? 'Нажмите' : 'Кликните';
    this._hintEl.textContent = pts.length === 0
      ? `${tapWord} на карту или объект`
      : pts.length === 1
        ? `${tapWord} в следующую точку`
        : `Отрезков: ${pts.length - 1}`;
    this._undoBtn.disabled = pts.length === 0;
    this._resetBtn.disabled = pts.length === 0;
  },
});

/**
 * Добавляет линейку на карту.
 * @param {L.Map} map
 * @param {{ isMobile?: boolean, position?: string, onEnable?: Function }} [options]
 */
export function setupMeasureTool(map, options = {}) {
  const control = new MeasureControl({
    position: options.position || 'topleft',
    isMobile: !!options.isMobile,
    onEnable: options.onEnable || null,
  });
  control.addTo(map);
  return control;
}
